import gql from 'graphql-tag'
import sortBy from 'lodash/sortBy'
import * as React from 'react'
import { useHistory, useParams } from 'react-router'

import { useQuery } from '../../api/graphql'
import useRecordLookup from '../../hooks/useRecordLookup'
import { usePageHidden } from '../../providers/HiddenPageContext'
import { placeFragmentWithEvents } from '../../graphql/fragments'
import Routes, { CommunitySlugRouteParams } from '../../startup/routes'
import { PublishState } from '../../util/constants'
import { RecordLoadFailed, RecordNotFound } from '../components/misc/RecordNotFound'
import PlaceView from '../components/places/PlaceView'

export const PlaceViewQuery = gql`
  query PlaceView($placeSlug: String!) {
    place(slug: $placeSlug) {
      ...placeFragmentWithEvents
    }
  }
  ${placeFragmentWithEvents}
`

// Keep in step with UPCOMING_EVENTS_LIMIT in app/graphql/types/place_type.rb.
const UPCOMING_EVENTS_LIMIT = 5

const PlaceViewContainer: React.FC = () => {
  const history = useHistory()
  const { idOrName: communitySlug, id: placeSlug } = useParams<CommunitySlugRouteParams & { id: string }>()

  const { loading, error, data: { place: foundPlace = null } = {} } = useQuery(PlaceViewQuery, {
    variables: { placeSlug },
    skip: !placeSlug,
    // The page says "Something went wrong" itself (CVL-145)
    context: { silentErrors: true },
  })
  // Under a town, a place that belongs to no town is not found, as for events and stories (CVL-145).
  const place = communitySlug && foundPlace && !foundPlace.communities?.length ? null : foundPlace
  const lookup = useRecordLookup({ loading, error, record: place })

  // A hidden page waits until it is shown before it moves the address (HiddenPageContext)
  const hidden = usePageHidden()
  React.useEffect(() => {
    if (hidden) return
    if (place && !place.isPremium) {
      history.push(Routes.place(placeSlug))
    } else if (place && place.slug !== placeSlug) {
      history.replace(Routes.premiumPlace(place))
    }
  }, [place, placeSlug, history, hidden])

  const events = React.useMemo(() => {
    if (!place) return []

    const now = new Date()

    // An event stays listed until it ends (or, with no end time, until it starts);
    // the list drops events that end later on, while the page stays open (UpcomingEvents).
    // Sort by the actual time: the strings can carry different UTC offsets around a DST change.
    return sortBy(
      place.events.nodes.filter(event => event.publishState === PublishState.APPROVED && now <= new Date(event.endAt || event.startAt)),
      event => new Date(event.startAt).getTime(),
    ).slice(0, UPCOMING_EVENTS_LIMIT)
  }, [place])

  if (lookup === 'failed') return <RecordLoadFailed />
  if (lookup === 'notFound') return <RecordNotFound kind="place" backTo={Routes.places(communitySlug)} backLabel="Back to Places" />
  return <PlaceView loading={loading} place={place} communityIdParam={communitySlug} events={events} />
}

export default PlaceViewContainer
